import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Search, ChevronRight, BarChart3, Sparkles, ExternalLink, ArrowLeft, Loader2 } from "lucide-react";

const Stat = ({ label, value, accent }) => (
  <div className="border border-pb-border bg-pb-elevated/40 px-3 py-2">
    <div className={`font-display font-black text-lg ${accent || "text-white"}`}>{value ?? "—"}</div>
    <div className="text-[9px] uppercase tracking-widest text-zinc-500">{label}</div>
  </div>
);

function Breakdown({ id }) {
  const [d, setD] = useState(null);
  const [advice, setAdvice] = useState(null);
  const [coaching, setCoaching] = useState(false);
  useEffect(() => { api.get(`/admin/tournament-intel/${id}`).then((r) => setD(r.data)).catch(() => {}); }, [id]);
  if (!d) return <div className="text-zinc-600 text-xs py-3 animate-pulse">Crunching numbers…</div>;
  const { particulars: p, participation: pa, operations: op, involvement: inv } = d;
  const coach = async () => {
    setCoaching(true);
    try { const { data } = await api.post(`/admin/tournament-intel/${id}/coach${advice ? "?refresh=true" : ""}`); setAdvice(data.advice); }
    catch (e) { toast.error(e.response?.data?.detail || "Coach unavailable"); }
    finally { setCoaching(false); }
  };
  return (
    <div className="border-t border-pb-border/50 pt-3 mt-2 space-y-4 text-sm">
      <div className="flex flex-wrap gap-x-5 gap-y-1 text-[11px] text-zinc-400">
        <span><b className="text-white">Director:</b> {p.director || "—"}</span>
        {p.co_directors?.length > 0 && <span><b className="text-white">Co-TDs:</b> {p.co_directors.map((c) => `${c.name}${c.full_access ? " (full)" : " (view)"}`).join(", ")}</span>}
        <span><b className="text-white">Divisions:</b> {(p.event_types || []).join(", ")} · {(p.levels || []).join(", ")}</span>
        <span><b className="text-white">Courts:</b> {(p.courts || []).join(", ") || "—"}</span>
        <span><b className="text-white">Fee:</b> ${p.entry_fee}</span>
        {p.reg_open_at && <span><b className="text-white">Reg window:</b> {new Date(p.reg_open_at).toLocaleString()} → {p.reg_close_at ? new Date(p.reg_close_at).toLocaleString() : "open"}</span>}
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-2" data-testid={`intel-stats-${id}`}>
        <Stat label="Entries" value={pa.entries} />
        <Stat label="Players" value={pa.unique_players} />
        <Stat label="Fill Rate" value={pa.fill_rate != null ? `${pa.fill_rate}%` : "—"} accent="text-pb-cyan" />
        <Stat label="Waitlist" value={pa.waitlist} />
        <Stat label="Matches" value={`${op.matches_played}/${op.matches_total}`} />
        <Stat label="Hands-Free" value={op.hands_free_pct != null ? `${op.hands_free_pct}%` : "—"} accent="text-lime-300" />
        <Stat label="TD-Entered" value={op.td_entered} />
        <Stat label="Avg Min/Score" value={op.avg_minutes_between_scores} />
        <Stat label="Bronze Matches" value={op.bronze_matches} />
        <Stat label="Notifications" value={inv.notifications_sent} />
        <Stat label="Gold Letters" value={inv.gold_letters} accent="text-yellow-400" />
        <Stat label="Est. Revenue" value={`$${inv.est_entry_revenue}`} accent="text-green-400" />
        <Stat label="Gallery Media" value={inv.gallery_published ? inv.gallery_media : "not published"} />
        <Stat label="Gallery ❤️" value={inv.gallery_likes} />
        <Stat label="Gallery Comments" value={inv.gallery_comments} />
        <Stat label="New Members ±7d" value={inv.new_members_around_event} accent="text-pb-cyan" />
      </div>
      <div className="flex flex-wrap gap-2 text-[10px] font-black uppercase tracking-widest">
        <a href={`/tournaments/${id}/report`} target="_blank" rel="noreferrer" className="border border-pb-cyan/60 text-pb-cyan px-3 py-1.5 hover:bg-pb-cyan/10 flex items-center gap-1"><ExternalLink className="w-3 h-3" /> Report</a>
        <a href={`/court-layout/${id}`} target="_blank" rel="noreferrer" className="border border-lime-400/60 text-lime-300 px-3 py-1.5 hover:bg-lime-400/10 flex items-center gap-1"><ExternalLink className="w-3 h-3" /> Court Layout</a>
        {inv.gallery_album_id && <a href={`/gallery/${inv.gallery_album_id}`} target="_blank" rel="noreferrer" className="border border-yellow-500/60 text-yellow-400 px-3 py-1.5 hover:bg-yellow-500/10 flex items-center gap-1"><ExternalLink className="w-3 h-3" /> Gallery</a>}
      </div>
      {d.pbr_points.length > 0 && (
        <div>
          <div className="text-[10px] font-black uppercase tracking-[0.25em] text-zinc-500 mb-1.5">National Run — PBR points earned here</div>
          <div className="grid sm:grid-cols-2 gap-x-6 gap-y-0.5 text-xs max-h-44 overflow-y-auto" data-testid={`intel-pbr-${id}`}>
            {d.pbr_points.map((r, i) => (
              <div key={i} className="flex justify-between border-b border-pb-border/40 py-0.5">
                <span className="text-zinc-300 truncate">{r.player} <span className="text-zinc-600">· {r.discipline}{r.finish ? ` · ${r.finish}` : ""}</span></span>
                <span className="font-mono font-bold text-pb-cyan">{r.points ?? 0} pts</span>
              </div>
            ))}
          </div>
        </div>
      )}
      <div>
        <button data-testid={`intel-coach-btn-${id}`} disabled={coaching} onClick={coach}
          className="flex items-center gap-2 bg-yellow-500 text-black text-[10px] font-black uppercase tracking-widest px-4 py-2.5 hover:bg-yellow-400 disabled:opacity-60">
          {coaching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          {coaching ? "Coach is reviewing the tape…" : advice ? "Re-run the Coach" : "Do Better Next Time — AI Coach"}
        </button>
        {advice && (
          <div className="mt-2 border border-yellow-500/40 bg-yellow-500/5 px-4 py-3 whitespace-pre-line text-[13px] text-zinc-200 leading-relaxed" data-testid={`intel-advice-${id}`}>
            {advice}
          </div>
        )}
      </div>
    </div>
  );
}

export default function TournamentIntel() {
  const [list, setList] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [openId, setOpenId] = useState(null);
  useEffect(() => {
    document.title = "Tournament Intelligence — Pickle Bullpen";
    api.get("/admin/tournament-intel").then((r) => setList(r.data)).catch(() => setList([]));
  }, []);
  const filtered = useMemo(() => (list || []).filter((t) => {
    const hay = `${t.name} ${t.director_name || ""} ${(t.co_directors || []).join(" ")} ${t.location || ""} ${t.date || ""}`.toLowerCase();
    return (!q || hay.includes(q.toLowerCase())) && (!status || t.status === status);
  }), [list, q, status]);
  return (
    <div data-testid="tournament-intel-page">
      <Link to="/admin" className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-zinc-500 hover:text-white mb-3">
        <ArrowLeft className="w-3.5 h-3.5" /> Control Center
      </Link>
      <div className="flex items-center gap-3 mb-1">
        <BarChart3 className="w-6 h-6 text-pb-cyan" />
        <h1 className="font-display font-black uppercase text-2xl">Tournament Intelligence</h1>
      </div>
      <p className="text-xs text-zinc-500 mb-4">Every real tournament, every number — participation, operations, site involvement, revenue, PBR points — plus an AI coach for running the next one better.</p>
      <div className="flex flex-wrap gap-2 mb-4">
        <div className="flex items-center gap-2 flex-1 min-w-[220px] bg-pb-elevated border border-pb-border px-3 py-2 focus-within:border-pb-cyan">
          <Search className="w-4 h-4 text-zinc-500" />
          <input data-testid="intel-search" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search by tournament, TD, location or date…"
            className="flex-1 bg-transparent text-sm text-white placeholder-zinc-600 focus:outline-none" />
        </div>
        <select data-testid="intel-status-filter" value={status} onChange={(e) => setStatus(e.target.value)}
          className="bg-pb-elevated border border-pb-border px-2 py-2 text-xs text-white focus:outline-none">
          <option value="">All statuses</option>
          <option value="open">Open</option>
          <option value="in_progress">In Progress</option>
          <option value="complete">Complete</option>
        </select>
      </div>
      {list === null ? <div className="text-zinc-600 animate-pulse text-sm">Loading…</div>
        : filtered.length === 0 ? <div className="text-zinc-600 text-sm border border-dashed border-pb-border p-8 text-center" data-testid="intel-empty">No tournaments match.</div>
        : filtered.map((t) => (
          <div key={t.id} className="border border-pb-border/70 mb-2 px-3 py-2.5" data-testid={`intel-row-${t.id}`}>
            <button data-testid={`intel-open-${t.id}`} onClick={() => setOpenId(openId === t.id ? null : t.id)}
              className="w-full flex items-center gap-3 text-left">
              <ChevronRight className={`w-4 h-4 text-zinc-500 transition-transform ${openId === t.id ? "rotate-90" : ""}`} />
              <span className="font-bold text-sm text-white flex-1 min-w-0 truncate">{t.name}</span>
              <span className="text-[10px] uppercase tracking-widest text-zinc-500">{t.date} · {t.location || "—"} · TD {t.director_name || "—"} · {t.entry_count} entries</span>
              <span className={`text-[9px] font-black uppercase tracking-widest px-2 py-0.5 border ${t.status === "complete" ? "border-zinc-600 text-zinc-400" : t.status === "in_progress" ? "border-lime-400 text-lime-300" : "border-pb-cyan text-pb-cyan"}`}>{t.status}</span>
            </button>
            {openId === t.id && <Breakdown id={t.id} />}
          </div>
        ))}
    </div>
  );
}
